import React, { useLayoutEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import { PrimaryButton, SecondaryButton } from "@prestosports/commons-ui";
import { EventStatus } from "@prestosports/prestostats-core/Common";
import Printable from "../../../Printable";
import ScoreBoard from "../../../../../game-stats/components/ScoreBoard";
import { toDisplayMs } from "../../../../../football/components/utils/PlayUtils";
import QuickStatsOptions from "./QuickStatsOptions";
import QuickStatsReport from "./QuickStatsReport";
import { buildQuickStats, DEFAULT_OPTIONS } from "./quick-stats-data";
import { PAGE_HEIGHT_MM, PAGE_STYLE } from "./QuickStats.styles";
import { useGameContext } from "../../../../../../state/game/context";
import { useGameContextContext } from "../../../../../../state/game-context/context";
import { useStatsContext } from "../../../../../../state/stats/context";
import { useTeamsContext } from "../../../../../../state/teams/context";

// CSS fixes 96px to 25.4mm, on screen and on paper alike
const CSS_PX_PER_MM = 96 / 25.4;

// Same header the Team Stats print opens with; props as BoxScore builds them
const QuickStatsScoreBoard = ({ boxScore }) => {
	const { gameContext, awayTeam, homeTeam, game, event } = boxScore;
	const { sportCode } = game;
	const { venue, startDateTime } = event;
	const { swapTeams, currentPeriod, gameClock, score, gameConfig } = gameContext;
	const [homeScore, visitorScore] = score || [0, 0];
	// Serialized payload: now is a number (canonical countdown ms).
	const now = toDisplayMs(
		typeof gameClock?.now === "function" ? gameClock.now() : gameClock?.now,
		gameConfig
	);
	const isFinal = EventStatus.isFinal(event);

	return (
		<ScoreBoard
			swapTeams={swapTeams}
			awayTeam={awayTeam}
			awayScore={visitorScore}
			homeTeam={homeTeam}
			homeScore={homeScore}
			startDateTime={startDateTime}
			venue={venue}
			periodNumber={currentPeriod}
			time={now}
			isFinal={isFinal}
			sportId={sportCode}
		/>
	);
};

// options -> measuring (offscreen render) -> printing, or -> overflow when
// the sheet runs past one page, where the user resizes or goes back
const QuickStatsPrint = ({ fileName, setIsLoading, onDone }) => {
	const { state: gameState } = useGameContext();
	const game = gameState.game();
	const { getBoxscoreJsonForGame } = useGameContextContext();
	const { state: statsState } = useStatsContext();
	const boxScore = statsState.boxScore();
	const lineScore = boxScore?.lineScore;
	const scoreBoard = boxScore?.gameContext ? (
		<QuickStatsScoreBoard boxScore={boxScore} />
	) : null;
	const { state: teamState } = useTeamsContext();
	const teamColors = (team) => ({
		background: team?.color,
		text: team?.contrastColor
	});
	const awayColors = teamColors(teamState.awayTeam());
	const homeColors = teamColors(teamState.homeTeam());

	const [options, setOptions] = useState(DEFAULT_OPTIONS);
	const [step, setStep] = useState("options");
	const [report, setReport] = useState(null);
	const [zoom, setZoom] = useState(1);
	const [error, setError] = useState(null);
	const measureRef = useRef();

	const backToOptions = () => {
		setIsLoading(false);
		setStep("options");
	};

	const handleGenerate = async () => {
		setError(null);
		setIsLoading(true);
		try {
			const boxscoreJson = await getBoxscoreJsonForGame(game?.id);
			setReport(buildQuickStats(boxscoreJson, lineScore, options));
			setZoom(1);
			setStep("measuring");
		} catch {
			setError("The report could not be generated.");
			setIsLoading(false);
		}
	};

	useLayoutEffect(() => {
		if (step !== "measuring" || !measureRef.current) {
			return;
		}
		const available = PAGE_HEIGHT_MM * CSS_PX_PER_MM;
		const height = measureRef.current.scrollHeight;
		if (height <= available) {
			setStep("printing");
		} else {
			// Small safety margin for rounding between screen and print layout
			setZoom(Math.floor((available / height) * 0.98 * 100) / 100);
			setStep("overflow");
		}
	}, [step]);

	return (
		<>
			{/* Stays on screen, disabled, while the hidden sheet is measured and
			printed - same as the other reports' option list */}
			{step !== "overflow" && (
				<QuickStatsOptions
					options={options}
					onChange={setOptions}
					onGenerate={handleGenerate}
					onBack={onDone}
					disabled={step !== "options"}
				/>
			)}
			{error && (
				<Typography color="error" variant="body2">
					{error}
				</Typography>
			)}

			{step === "overflow" && (
				<Box>
					<Typography variant="h6">Report too large</Typography>
					<Typography variant="body2">
						The report data is too large for a single sheet. Resize the content to fit
						one page, or cancel and adjust the report options.
					</Typography>
					<Box mt={2} display="flex" justifyContent="flex-end" gridGap={8}>
						<SecondaryButton label="Cancel" size="medium" onClick={backToOptions} />
						<PrimaryButton
							label="Resize to fit"
							size="medium"
							onClick={() => setStep("printing")}
						/>
					</Box>
				</Box>
			)}

			{step === "measuring" &&
				ReactDOM.createPortal(
					<div
						ref={measureRef}
						aria-hidden
						style={{ position: "fixed", left: -10000, top: 0, visibility: "hidden" }}
					>
						<QuickStatsReport
							report={report}
							awayColors={awayColors}
							homeColors={homeColors}
							scoreBoard={scoreBoard}
						/>
					</div>,
					document.body
				)}

			{step === "printing" && (
				<Printable
					showPreview
					fileName={`Quick_Stats-${fileName}`}
					pageStyle={PAGE_STYLE}
					onAfterPrint={onDone}
				>
					<QuickStatsReport
						report={report}
						awayColors={awayColors}
						homeColors={homeColors}
						scoreBoard={scoreBoard}
						zoom={zoom}
					/>
				</Printable>
			)}
		</>
	);
};

export default QuickStatsPrint;
